import { create, useStore } from 'zustand'
import { persist } from 'zustand/middleware'

import { type AllStepsData } from './steps/AllStepsData'

type TestData = AllStepsData

type UnsophisticatedTestStorage = {
  testData: TestData | null
  updateTestData: (testData: TestData) => void

  currentStepIndex: number
  goForward: () => void
  goBackward: () => void

  reset: () => void
  resetCurrentStep: () => void
  remove: () => void
}

// Mapa store-ov podľa userId - zaisťuje, že každý používateľ má svoj vlastný zdieľaný store
const stores = new Map<string, ReturnType<typeof _createStore>>()

export function useUnsophisticatedTestStore(userId: string | null) {
  if (!userId) {
    throw new Error('User ID is missing')
  }

  // Ak už existuje store pre tohto používateľa, vráť ho, inak vytvor nový
  if (!stores.has(userId)) {
    stores.set(userId, _createStore(userId))
  }

  const store = stores.get(userId)!
  // Použijeme useStore hook na reaktívne subskribovanie na zmeny v store
  // Vrátime state aj metódy z store objektu
  const state = useStore(store)
  return {
    ...state,
    updateTestData: store.getState().updateTestData,
    goForward: store.getState().goForward,
    goBackward: store.getState().goBackward,
    reset: store.getState().reset,
    resetCurrentStep: store.getState().resetCurrentStep,
    remove: store.getState().remove,
  }
}

function _createStore(userId: string) {
  // `-v1`: nový kľúč zahodil medzistavy rozpracované pred ukladaním dotazníka do API
  const storageName = `unsophisticated-analysis-test-${userId}-v1`
  const storage = {
    getItem: (name: string) => {
      const str = localStorage.getItem(name)
      return str ? JSON.parse(str) : null
    },
    setItem: (name: string, value: any) => {
      localStorage.setItem(name, JSON.stringify(value))
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name)
    },
  }

  return create<UnsophisticatedTestStorage>()(
    persist(
      (set, get) => ({
        testData: null,
        updateTestData: (updatedTestData) =>
          set({ testData: { ...get().testData, ...updatedTestData } }),

        currentStepIndex: 0,
        goForward: () => set({ currentStepIndex: get().currentStepIndex + 1 }),
        goBackward: () => set({ currentStepIndex: get().currentStepIndex - 1 }),

        reset: () => set({ currentStepIndex: 0, testData: null }),
        resetCurrentStep: () => set({ currentStepIndex: 0 }),
        remove: () => {
          // Použitie nativného Zustand persist storage API - najprv vyčistíme storage
          storage.removeItem(storageName)
          // Potom odstránime store z Mapy
          stores.delete(userId)
        },
      }),
      {
        name: storageName,
        storage,
      }
    )
  )
}
